/**
 * @file PersonPhoto.tsx
 * @brief A person's photo inside a hand-built avatar circle, with a fallback.
 *
 * @details
 * DES (01-10-2026): "yeh kuch images nahiin aa rahin, menu main yaa list
 * main". A broken-image glyph showed where a colleague's photo should be: the
 * photo was their Google account picture (lh3.googleusercontent.com), which
 * Google may refuse when the request carries a Referer from another site
 * (the desktop app especially). Two fixes, here and in `Avatar`:
 * - `referrerPolicy="no-referrer"`, so the photo host sees no Referer;
 * - if it still fails (expired signed URL, deleted picture), the caller's
 *   `fallback` (initials) shows, never the browser's broken-image glyph.
 *
 * @author DES
 * @date 01-10-2026
 */

import { useEffect, useState, type ReactNode } from "react";
import { cn } from "@/shared/theme/cn";

export function PersonPhoto({
  src,
  fallback,
  className,
}: {
  src: string | null | undefined;
  /** What the circle shows with no photo or a failed one (initials). */
  fallback: ReactNode;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) return <>{fallback}</>;
  return (
    <img
      src={src}
      alt=""
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
      className={cn("h-full w-full object-cover", className)}
    />
  );
}
